import { APP_SETTINGS } from '../config/appSettings'

/**
 * When may a page be read for its gist? Reading costs money and time, so it is decided here, in one
 * pure function over an ordered rule list kept in `appSettings.json` (`links.gist`). Tuning the
 * behaviour means editing that list, not code. No model call lives in this file.
 *
 * Rules are checked in order; the first whose `when` matches decides. Inside `when`, every field
 * must match (AND) and an array field matches if any entry does (OR). A field left out is ignored.
 * After a `read` decision, hard limits (attempts, back-off, daily budget) can still veto it.
 */

export type GistTrigger = 'arrival' | 'note-open' | 'card-visible' | 'peek' | 'row-expand' | 'manual' | 'background'
export type GistOrigin = 'share' | 'paste' | 'typed' | 'import' | 'sync'
export type GistAction = 'read' | 'skip' | 'reuse'

export interface GistContext {
  trigger: GistTrigger
  origin: GistOrigin
  /** Page kind from link metadata, null when unknown. */
  pageKind: string | null
  domain: string | null
  /** The link already has a gist. */
  hasGist: boolean
  /** Another note already holds a gist for the same canonical URL, so it can be copied. */
  cached: boolean
  /** Title and image are in; the cheap preview must succeed before any spend. */
  metadataReady: boolean
  linksInNote: number
  online: boolean
  metered: boolean
  attemptCount: number
  lastAttemptAt: number | null
  /** Reads already made today by this account. */
  readsToday: number
  now: number
}

export interface GistWhen {
  trigger?: GistTrigger[]
  origin?: GistOrigin[]
  pageKind?: string[]
  /** Host or registrable suffix, e.g. "instagram.com" also matches "www.instagram.com". */
  domain?: string[]
  hasGist?: boolean
  cached?: boolean
  metadataReady?: boolean
  online?: boolean
  metered?: boolean
  linksInNoteMin?: number
  linksInNoteMax?: number
}

export interface GistRule { id: string; when: GistWhen; do: GistAction }

/** How the Worker reads a page's text through Jina Reader (worker/linkPageText.ts). */
export interface GistReaderSettings {
  baseUrl: string
  maxTokens: number
  timeoutSeconds: number
  targetSelector: string
  /** Elements dropped before extraction (X-Remove-Selector). Not `header`: it often holds the article's own title. */
  removeSelector: string
  minUsableChars: number
  escalateToBrowser: boolean
  /** Ask Reader for a fresh read: its cache once returned another site's page for example.com. */
  noCache: boolean
  /** Hosts never sent to Reader (login-gated; a read only wastes time). */
  skipDomains: string[]
}

/** Server-side spend limits for the gist route (worker/gistBudget.ts). Money is in millionths of a US dollar. */
export interface GistBudgetSettings {
  dailyReadsNormal: number
  dailyReadsAdmin: number
  /** Total for the calendar month, across every account. */
  monthlyMicrousd: number
  /** Held per read until its real cost is known; sized above a typical read so the cap can only be conservative. */
  reservationMicrousd: number
  /** Reader's price per million tokens, to cost a read; confirm on the key's dashboard. */
  readerMicrousdPerMillionTokens: number
}

/** The one model call that turns capped page text into the gist sentence. */
export interface GistSummarySettings {
  apiBaseUrl: string
  model: string
  maxOutputTokens: number
  requestTimeoutMs: number
  maxResponseBytes: number
  maxChars: number
  cacheSeconds: number
}

/** "Ask about this page": the person's own question about a page, answered at more length than the gist. */
export interface GistAskSettings {
  maxOutputTokens: number
  requestTimeoutMs: number
  maxResponseBytes: number
  maxPromptChars: number
  maxAnswerChars: number
  /** Answers kept on a link; the oldest is dropped when a new one arrives. */
  maxStoredPerLink: number
  /** Held for a question until its real cost is known: an answer can be 5000 tokens, far more than a gist. */
  reservationMicrousd: number
}

export interface GistSettings {
  enabled: boolean
  maxAttempts: number
  retryBackoffMs: number
  /** How long a card or row must stay on screen before it counts as attention. */
  visibleDwellMs: number
  dailyReadsPerAccount: number
  maxConcurrentReads: number
  reader: GistReaderSettings
  summary: GistSummarySettings
  ask: GistAskSettings
  budget: GistBudgetSettings
  rules: GistRule[]
}

export interface GistDecision { action: GistAction; rule: string }

const ACTIONS: readonly string[] = ['read', 'skip', 'reuse']
const WHEN_KEYS = new Set(['trigger', 'origin', 'pageKind', 'domain', 'hasGist', 'cached', 'metadataReady', 'online', 'metered', 'linksInNoteMin', 'linksInNoteMax'])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Reads the settings group strictly: a malformed rule list disables the feature instead of guessing. */
export function parseGistSettings(raw: unknown): GistSettings {
  const off: GistSettings = { enabled: false, maxAttempts: 0, retryBackoffMs: 0, visibleDwellMs: 0, dailyReadsPerAccount: 0, maxConcurrentReads: 0, reader: { baseUrl: '', maxTokens: 0, timeoutSeconds: 0, targetSelector: '', removeSelector: '', minUsableChars: 0, escalateToBrowser: false, noCache: false, skipDomains: [] }, summary: { apiBaseUrl: '', model: '', maxOutputTokens: 0, requestTimeoutMs: 0, maxResponseBytes: 0, maxChars: 0, cacheSeconds: 0 }, ask: { maxOutputTokens: 0, requestTimeoutMs: 0, maxResponseBytes: 0, maxPromptChars: 0, maxAnswerChars: 0, maxStoredPerLink: 0, reservationMicrousd: 0 }, budget: { dailyReadsNormal: 0, dailyReadsAdmin: 0, monthlyMicrousd: 0, reservationMicrousd: 0, readerMicrousdPerMillionTokens: 0 }, rules: [] }
  if (!isRecord(raw) || !Array.isArray(raw.rules)) return off
  const numbers = ['maxAttempts', 'retryBackoffMs', 'visibleDwellMs', 'dailyReadsPerAccount', 'maxConcurrentReads'] as const
  if (typeof raw.enabled !== 'boolean' || numbers.some((key) => typeof raw[key] !== 'number' || !(raw[key] as number >= 0))) return off
  const rules: GistRule[] = []
  for (const entry of raw.rules) {
    if (!isRecord(entry) || typeof entry.id !== 'string' || !isRecord(entry.when)
      || typeof entry.do !== 'string' || !ACTIONS.includes(entry.do)
      || Object.keys(entry.when).some((key) => !WHEN_KEYS.has(key))) return off
    rules.push({ id: entry.id, when: entry.when as GistWhen, do: entry.do as GistAction })
  }
  const reader = raw.reader
  if (!isRecord(reader) || typeof reader.baseUrl !== 'string' || !/^https:\/\//.test(reader.baseUrl)
    || ['maxTokens', 'timeoutSeconds', 'minUsableChars'].some((key) => typeof reader[key] !== 'number' || !(reader[key] as number > 0))
    || typeof reader.targetSelector !== 'string' || typeof reader.removeSelector !== 'string' || typeof reader.escalateToBrowser !== 'boolean' || typeof reader.noCache !== 'boolean'
    || !Array.isArray(reader.skipDomains) || reader.skipDomains.some((entry) => typeof entry !== 'string')) return off
  const summary = raw.summary
  if (!isRecord(summary) || typeof summary.apiBaseUrl !== 'string' || !/^https:\/\//.test(summary.apiBaseUrl)
    || typeof summary.model !== 'string' || !summary.model
    || ['maxOutputTokens', 'requestTimeoutMs', 'maxResponseBytes', 'maxChars', 'cacheSeconds'].some((key) => typeof summary[key] !== 'number' || !(summary[key] as number > 0))) return off
  const ask = raw.ask
  if (!isRecord(ask) || ['maxOutputTokens', 'requestTimeoutMs', 'maxResponseBytes', 'maxPromptChars', 'maxAnswerChars', 'maxStoredPerLink', 'reservationMicrousd']
    .some((key) => typeof ask[key] !== 'number' || !(ask[key] as number > 0))) return off
  const budget = raw.budget
  if (!isRecord(budget) || ['dailyReadsNormal', 'dailyReadsAdmin', 'monthlyMicrousd', 'reservationMicrousd', 'readerMicrousdPerMillionTokens']
    .some((key) => typeof budget[key] !== 'number' || !(budget[key] as number > 0))) return off
  return { ...(raw as unknown as GistSettings), reader: reader as unknown as GistReaderSettings, summary: summary as unknown as GistSummarySettings, ask: ask as unknown as GistAskSettings, budget: budget as unknown as GistBudgetSettings, rules }
}

export const GIST_SETTINGS: GistSettings = parseGistSettings(APP_SETTINGS.links.gist)

function domainMatches(domain: string | null, entries: string[]): boolean {
  if (!domain) return false
  const host = domain.toLowerCase().replace(/^www\./, '')
  return entries.some((entry) => host === entry || host.endsWith(`.${entry}`))
}

export function gistRuleMatches(when: GistWhen, context: GistContext): boolean {
  if (when.trigger && !when.trigger.includes(context.trigger)) return false
  if (when.origin && !when.origin.includes(context.origin)) return false
  if (when.pageKind && !(context.pageKind !== null && when.pageKind.includes(context.pageKind))) return false
  if (when.domain && !domainMatches(context.domain, when.domain)) return false
  if (when.hasGist !== undefined && when.hasGist !== context.hasGist) return false
  if (when.cached !== undefined && when.cached !== context.cached) return false
  if (when.metadataReady !== undefined && when.metadataReady !== context.metadataReady) return false
  if (when.online !== undefined && when.online !== context.online) return false
  if (when.metered !== undefined && when.metered !== context.metered) return false
  if (when.linksInNoteMin !== undefined && context.linksInNote < when.linksInNoteMin) return false
  if (when.linksInNoteMax !== undefined && context.linksInNote > when.linksInNoteMax) return false
  return true
}

export function decideGistRead(context: GistContext, settings: GistSettings = GIST_SETTINGS): GistDecision {
  if (!settings.enabled) return { action: 'skip', rule: 'disabled' }
  const rule = settings.rules.find((candidate) => gistRuleMatches(candidate.when, context))
  if (!rule) return { action: 'skip', rule: 'no-rule' }
  if (rule.do !== 'read') return { action: rule.do, rule: rule.id }
  if (context.attemptCount >= settings.maxAttempts) return { action: 'skip', rule: 'limit:attempts' }
  if (context.lastAttemptAt !== null && context.now - context.lastAttemptAt < settings.retryBackoffMs) {
    return { action: 'skip', rule: 'limit:backoff' }
  }
  if (context.readsToday >= settings.dailyReadsPerAccount) return { action: 'skip', rule: 'limit:budget' }
  return { action: 'read', rule: rule.id }
}
